import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Input } from '../components/ui/input';
import {
  LogIn, Database, Lock, Mail, ShieldCheck, AlertTriangle, Hourglass,
  Bell, ShoppingCart, BarChart3, Apple, Zap,
  MessageSquare, MessageCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { girisIntroIsaretle } from '../components/kalibre/GirisIntro';
import { getDeviceFingerprint, getDeviceType, suggestDeviceLabel, getDeviceFingerprintExtras } from '../utils/deviceFingerprint';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const musteriMi = (u) => u?.role === 'client' || u?.role === 'musteri';

const FONT_SANS = { fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" };

// 📱 Mobil uygulama mağaza linkleri — GERÇEK LİNKLERİ BURAYA YAPIŞTIR.
// Boş bırakılırsa buton pasif görünür ("Yakında mağazada").
const APP_STORE_URL = 'https://apps.apple.com/tr/app/yetsan-panel/id6769400988';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.yetsan.panel';
const APP_QR_URL = ''; // (opsiyonel) QR görseli URL'i — verilirse sağda "QR ile indir" gösterilir

const Login = () => {
  const navigate = useNavigate();
  const { login, otpIste, otpGiris, sifreBelirle, otpLoginUygula, setUser, setToken } = useAuth();

  const [step, setStep] = useState('creds');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otpCode, setOtpCode] = useState('');
  // 🔑 Müşteri şifresiz OTP girişi
  const [musteriOtpModu, setMusteriOtpModu] = useState(false);
  const [musteriOtpGonderildi, setMusteriOtpGonderildi] = useState(false);
  const [musteriOtp, setMusteriOtp] = useState('');
  const [sifreBelirleModu, setSifreBelirleModu] = useState(false);
  const [yeniSifre, setYeniSifre] = useState('');
  const [yeniSifreTekrar, setYeniSifreTekrar] = useState('');
  // OTP'den dönen, şifre belirlenene kadar UYGULANMAYAN giriş bilgisi
  const [otpToken, setOtpToken] = useState('');
  const [otpUser, setOtpUser] = useState(null);
  const [deviceLabel, setDeviceLabel] = useState(suggestDeviceLabel());
  const [maskedEmail, setMaskedEmail] = useState('');
  const [kanallar, setKanallar] = useState([]);      // [{kanal, aktif, hedef}]
  const [otpKanal, setOtpKanal] = useState('');       // kullanılan kanal
  const [otpHedef, setOtpHedef] = useState('');       // maskeli hedef
  const [statusMsg, setStatusMsg] = useState('');
  const [loading, setLoading] = useState(false);

  // 🆕 Mesai dışı state (Feb 2026)
  const [mesaiDisiInfo, setMesaiDisiInfo] = useState(null); // { request_id, manager_email_masked, neden, ... }
  const [mesaiDisiOnaylayicilar, setMesaiDisiOnaylayicilar] = useState([]);
  const [mesaiDisiDurations, setMesaiDisiDurations] = useState({});
  const [mesaiDisiSecimDk, setMesaiDisiSecimDk] = useState(60);
  const [mesaiDisiSecimOnaylayici, setMesaiDisiSecimOnaylayici] = useState('');
  const [mesaiDisiNeden, setMesaiDisiNeden] = useState('');
  const [mesaiDisiSubmitting, setMesaiDisiSubmitting] = useState(false);
  const mesaiPollRef = useRef(null);

  // 📧 Maildeki "Panele Giriş Yap" linki → doğrudan "kod ile giriş" ekranı.
  // ?giris=kod&email=...  → OTP modu açık, e-posta dolu, kod giriş alanı görünür
  // (kod zaten mailde geldiği için "Kod Gönder" adımını atlar; süre dolduysa
  // "Yeni kod gönder" ile tazeler).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('giris') === 'kod') {
      setMusteriOtpModu(true);
      setMusteriOtpGonderildi(true);
      const e = params.get('email');
      if (e) setEmail(e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Giriş sonrası tek yönlendirme — herkes Yetsan açılışıyla (lazer kazıma → ortadan ayrılma) girer, açılış
  // girişte bir kez oynar. Müşteri kendi paneline (2026-10-05: "müşteri paneli de öyle açılsın"); personel
  // Sistem Seç'e; müdür kendi panosuna.
  const girisYonlendir = (u) => {
    girisIntroIsaretle();
    if (u?.kullanici_tipi === 'mes') { navigate('/mes'); return; }      // yalnız MES'e giren hesap: panel / Sistem Seç yok
    if (musteriMi(u)) { navigate(u?.kullanici_tipi === 'kk_odeme' ? '/panel/musteri-cari' : '/panel/anasayfa'); return; }
    navigate(u?.role === 'mudur' ? '/yonetici-dashboard' : '/sistem-sec');
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) { toast.error('Email ve şifre gerekli'); return; }
    setLoading(true);
    const result = await login(email, password);
    if (result.success) {
      if (result.must_change_password) {
        toast.warning('Güvenlik için şifrenizi güncellemeniz gerekiyor', { duration: 4500 });
        navigate('/panel/sifre-degistir?force=1');
      } else {
        girisYonlendir(result.user);          // "Giriş başarılı" bildirimi yerine Yetsan açılışı
      }
    } else if (result.device_trust_required) {
      if (result.status === 'new_device') await requestOtp();
      else if (result.status === 'pending_approval' || result.status === 'pending_admin') {
        setStep('pending'); setStatusMsg(result.error || 'Cihazınız admin onayı bekliyor.');
      } else if (result.status === 'blocked_concurrent_ip') {
        setStep('blocked'); setStatusMsg(result.error || 'Anormal aktivite tespit edildi.');
      } else if (result.status === 'limit_exceeded') {
        setStep('pending'); setStatusMsg(result.error || 'Cihaz limitiniz dolu.');
      } else toast.error(result.error || 'Giriş başarısız');
    } else if (result.mesai_disi) {
      // 🆕 Mesai dışı giriş — önce onaylayıcı seçim formu
      await loadMesaiDisiSecim(result.neden);
    } else if (result.code === 'ilk_giris_otp') {
      // 🆕 Müşteri ilk giriş: geçici şifre (YETSAN) doğru → doğrudan kod giriş ekranına geç
      // (Kod zaten mail+SMS ile gönderildi.)
      if (result.email) setEmail(result.email);
      setMusteriOtp('');
      setMusteriOtpModu(true);
      setMusteriOtpGonderildi(true);
      toast.info(result.message || 'İlk girişiniz — size gönderdiğimiz doğrulama kodunu girin', { duration: 5000 });
    } else toast.error(result.error || 'Giriş başarısız');
    setLoading(false);
  };

  // 🆕 Mesai dışı: önce seçim formunu yükle (süre + onaylayıcı listesi)
  const loadMesaiDisiSecim = async (neden) => {
    try {
      const r = await axios.post(`${API}/auth/mesai-disi/onaylayicilar`, { email, password });
      if (r.data?.success) {
        const list = r.data.onaylayicilar || [];
        setMesaiDisiOnaylayicilar(list);
        setMesaiDisiDurations(r.data.duration_options || {});
        setMesaiDisiNeden(neden || '');
        // Default seçim: ilk öncelikli (Müdürün) - YOKSA ilk admin
        const ilk = list.find(o => o.oncelik) || list[0];
        setMesaiDisiSecimOnaylayici(ilk?.id || '');
        setMesaiDisiSecimDk(60);
        // Önceki info'yu temizle (state karışmasın)
        setMesaiDisiInfo(null);
        setStep('mesai_disi_secim');
      } else {
        toast.error(r.data?.error || 'Onaylayıcı listesi alınamadı');
      }
    } catch (err) {
      toast.error('Hata: ' + (err.response?.data?.detail || err.message));
    }
  };

  // 🆕 Seçim sonrası onay isteği gönder
  const submitMesaiDisiIstek = async () => {
    if (!mesaiDisiSecimOnaylayici) { toast.error('Lütfen bir onaylayıcı seçin'); return; }
    setMesaiDisiSubmitting(true);
    try {
      const r = await axios.post(`${API}/auth/mesai-disi/request`, {
        email,
        password,
        requested_dk: mesaiDisiSecimDk,
        onaylayici_id: mesaiDisiSecimOnaylayici,
      });
      if (r.data.success) {
        setMesaiDisiInfo({
          request_id: r.data.request_id,
          manager_email_masked: r.data.manager_email_masked,
          manager_name: r.data.manager_name,
          manager_role: r.data.manager_role,
          talep_edilen_dk: r.data.talep_edilen_dk,
          neden: mesaiDisiNeden,
          expires_in_minutes: r.data.expires_in_minutes,
          duplicate: !!r.data.duplicate,
        });
        setStep('mesai_disi');
        setStatusMsg(r.data.message);
        startMesaiDisiPolling(r.data.request_id);
      } else {
        toast.error(r.data.error || 'Onay isteği gönderilemedi');
      }
    } catch (err) {
      toast.error('İstek hatası: ' + (err.response?.data?.detail || err.message));
    }
    setMesaiDisiSubmitting(false);
  };

  const startMesaiDisiPolling = (requestId) => {
    if (mesaiPollRef.current) clearInterval(mesaiPollRef.current);
    mesaiPollRef.current = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/auth/mesai-disi/durum/${requestId}`);
        if (!r.data?.success) return;
        if (r.data.status === 'onaylandi') {
          clearInterval(mesaiPollRef.current); mesaiPollRef.current = null;
          toast.success(`Müdürünüz ${r.data.onay_suresi_dk} dk giriş onayladı!`);
          // Login'i tekrar dene — backend bu sefer geçirecek
          setLoading(true);
          const result2 = await login(email, password);
          setLoading(false);
          if (result2.success) {
            girisYonlendir(result2.user);
          } else {
            toast.error(result2.error || 'Otomatik giriş başarısız, tekrar deneyin');
            setStep('creds');
          }
        } else if (r.data.status === 'reddedildi') {
          clearInterval(mesaiPollRef.current); mesaiPollRef.current = null;
          toast.error('Müdürünüz girişi reddetti: ' + (r.data.red_nedeni || ''));
          setStep('creds');
        } else if (r.data.status === 'expire') {
          clearInterval(mesaiPollRef.current); mesaiPollRef.current = null;
          toast.warning('Onay süresi doldu. Lütfen tekrar deneyin.');
          setStep('creds');
        }
      } catch (e) { /* polling silent */ }
    }, 3000);
  };

  // Cleanup
  useEffect(() => () => { if (mesaiPollRef.current) clearInterval(mesaiPollRef.current); }, []);

  const KANAL_AD = { sms: 'SMS', whatsapp: 'WhatsApp', mail: 'E-posta' };

  const requestOtp = async (kanal) => {
    setLoading(true);
    try {
      const fp = await getDeviceFingerprint();
      const dt = getDeviceType();
      const ua = navigator.userAgent.slice(0, 250);
      const fpExtra = getDeviceFingerprintExtras();
      const r = await axios.post(`${API}/auth/device/request-otp`, {
        email, password, fingerprint: fp, device_type: dt,
        user_agent: ua, requested_label: deviceLabel, fp_extra: fpExtra,
        ...(kanal ? { kanal } : {})
      });
      if (r.data.success) {
        setMaskedEmail(r.data.email_masked || '');
        if (Array.isArray(r.data.kanallar)) setKanallar(r.data.kanallar);
        setOtpKanal(r.data.kanal || '');
        setOtpHedef(r.data.hedef_masked || r.data.email_masked || '');
        setStep('otp');
        const kanalAd = KANAL_AD[r.data.kanal] || 'kanal';
        toast.success(`Aktivasyon kodu ${kanalAd} ile ${r.data.hedef_masked || r.data.email_masked} adresine gönderildi`);
      } else if (r.data.status === 'limit_exceeded') {
        setStep('pending'); setStatusMsg(r.data.error);
      } else toast.error(r.data.error || 'Kod gönderilemedi');
    } catch (err) { toast.error('Kod gönderim hatası: ' + (err.response?.data?.detail || err.message)); }
    setLoading(false);
  };

  // 🔑 Müşteri: giriş kodu iste
  const handleMusteriOtpIste = async (e) => {
    if (e) e.preventDefault();
    if (!email) { toast.error('E-posta girin'); return; }
    setLoading(true);
    const r = await otpIste(email);
    setLoading(false);
    if (r.success) {
      setMusteriOtpGonderildi(true);
      toast.success('Giriş kodu e-postanıza gönderildi (30 dk geçerli)');
    } else {
      toast.error(r.error || 'Kod gönderilemedi');
    }
  };

  // 🔑 Müşteri: kod ile giriş
  const handleMusteriOtpGiris = async (e) => {
    if (e) e.preventDefault();
    if (!musteriOtp || musteriOtp.length < 4) { toast.error('Kodu girin'); return; }
    setLoading(true);
    const r = await otpGiris(email, musteriOtp.trim());
    setLoading(false);
    if (r.success) {
      // İlk giriş → kendi şifresini belirlesin (OTP'yi sadece aktivasyon için kullandı)
      if (r.must_change_password) {
        // Girişi henüz uygulama; token+user'ı sakla, şifre belirlenince tamamla
        setOtpToken(r.token || '');
        setOtpUser(r.user || null);
        setSifreBelirleModu(true);
        toast.success('Kod doğrulandı. Şimdi kendi şifrenizi belirleyin.');
      } else {
        girisIntroIsaretle();                 // müşteri kodla girişte de Yetsan açılışı
        navigate('/panel/anasayfa');
      }
    } else {
      if (r.code === 'otp_suresi_doldu' || r.code === 'otp_kilitli' || r.code === 'otp_yok') {
        setMusteriOtp('');
        toast.error((r.error || 'Kod geçersiz') + ' — yeni kod isteyin');
      } else {
        toast.error(r.error || 'Kod hatalı');
      }
    }
  };

  // 🔑 Müşteri: ilk girişte kendi şifresini belirler
  const handleSifreBelirle = async (e) => {
    if (e) e.preventDefault();
    if (!yeniSifre || yeniSifre.length < 6) { toast.error('Şifre en az 6 karakter olmalı'); return; }
    if (yeniSifre !== yeniSifreTekrar) { toast.error('Şifreler eşleşmiyor'); return; }
    setLoading(true);
    const r = await sifreBelirle(yeniSifre, otpToken);
    setLoading(false);
    if (r.success) {
      // Şifre belirlendi → şimdi girişi tamamla (token uygula) ve panele git
      otpLoginUygula(otpToken, otpUser);
      toast.success('Şifreniz belirlendi.');
      girisIntroIsaretle();                   // ilk giriş: şifre belirlenince Yetsan açılışıyla panele
      navigate('/panel/anasayfa');
    } else {
      toast.error(r.error || 'Şifre belirlenemedi');
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (!otpCode || otpCode.length < 4) { toast.error('Lütfen 6 haneli kodu girin'); return; }
    setLoading(true);
    try {
      const fp = await getDeviceFingerprint();
      const r = await axios.post(`${API}/auth/device/verify-otp`, {
        email, code: otpCode, fingerprint: fp, device_label: deviceLabel
      });
      if (r.data.success) {
        if (r.data.token) {
          toast.success('Giriş başarılı! Cihazınız kaydedildi.');
          // 🔧 FIX: verify-otp zaten token döndürdü. Tekrar login(email,password) çağrılması
          // race condition'a sebep oluyor (token DB'ye yazıldı ama state'te yok).
          localStorage.setItem('token', r.data.token);
          setToken(r.data.token);
          if (r.data.user) {
            localStorage.setItem('user', JSON.stringify(r.data.user));
            setUser(r.data.user);
          }
          girisYonlendir(r.data.user);
        } else if (r.data.status === 'pending_approval') {
          setStep('pending'); setStatusMsg(r.data.message);
          toast.success('Doğrulama başarılı. Admin onayı bekleniyor.');
        }
      } else if (r.data.mesai_disi) {
        // Cihaz doğrulandı ama mesai dışı → müdür onay akışına geç
        await loadMesaiDisiSecim(r.data.neden);
      } else toast.error(r.data.error || 'Kod yanlış');
    } catch (err) { toast.error('Doğrulama hatası: ' + (err.response?.data?.detail || err.message)); }
    setLoading(false);
  };

  const handleVerifyAdminOtp = async (e) => {
    e.preventDefault();
    if (!otpCode || otpCode.length < 6) { toast.error('Lütfen yöneticinizden aldığınız 6 haneli kodu girin'); return; }
    setLoading(true);
    try {
      const fp = await getDeviceFingerprint();
      const ua = navigator.userAgent.slice(0, 250);
      const fpExtra = getDeviceFingerprintExtras();
      const r = await axios.post(`${API}/auth/device/verify-admin-otp`, {
        email, password, code: otpCode, fingerprint: fp, device_label: deviceLabel, user_agent: ua, fp_extra: fpExtra
      });
      if (r.data.success && r.data.token) {
        toast.success('Giriş başarılı! Admin onayıyla cihazınız aktif.');
        // 🔧 FIX: verify-admin-otp zaten token + user döndürüyor.
        // Eski hatalı akışta tekrar login(email,password) çağrılınca Device Trust
        // henüz state'e işlemediği için "yeni cihaz" hatası veriyordu.
        // Direkt AuthContext'e set ediyoruz.
        localStorage.setItem('token', r.data.token);
        setToken(r.data.token);
        if (r.data.user) {
          localStorage.setItem('user', JSON.stringify(r.data.user));
          setUser(r.data.user);
        }
        girisYonlendir(r.data.user);
      } else toast.error(r.data.error || 'Kod yanlış');
    } catch (err) { toast.error('Doğrulama hatası: ' + (err.response?.data?.detail || err.message)); }
    setLoading(false);
  };

  const resetFlow = () => { setStep('creds'); setOtpCode(''); setStatusMsg(''); setMaskedEmail(''); setKanallar([]); setOtpKanal(''); setOtpHedef(''); };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#fffaf9] via-[#fef2f2] to-[#fff5f5] relative overflow-hidden" style={FONT_SANS}>
      {/* Ambient glows */}
      <div className="absolute -top-32 -left-32 w-[480px] h-[480px] bg-red-200/40 rounded-full blur-[120px]" />
      <div className="absolute -bottom-32 -right-32 w-[480px] h-[480px] bg-rose-200/40 rounded-full blur-[120px]" />

      <div className="relative min-h-screen flex items-center justify-center px-4 py-8">
        {/* Compact 2-column container — daha sıkı yerleşim */}
        <div className="w-full max-w-6xl grid lg:grid-cols-[1.05fr_1fr] gap-10 lg:gap-12 items-center">

          {/* ===== SOL — TEASER ===== (mobilde giriş kartının ALTINA düşer) */}
          <div className="order-2 lg:order-1 text-center lg:text-left">
            {/* "Şimdi Yayında" rozeti */}
            <div className="flex items-center justify-center lg:justify-start gap-3 mb-6">
              <div className="inline-flex items-center gap-2 bg-emerald-50 border border-emerald-200/80 rounded-full px-3.5 py-1.5 shadow-sm" data-testid="live-badge">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                </span>
                <span className="text-[10.5px] font-bold text-emerald-700 tracking-[0.18em] uppercase">Şimdi Yayında</span>
              </div>
            </div>

            <h1 className="text-slate-900 leading-[0.95] tracking-tight">
              <span className="block text-[38px] sm:text-[52px] lg:text-[64px] font-extrabold">
                Yetsan Panel
              </span>
              <span className="block mt-1 text-[38px] sm:text-[52px] lg:text-[64px] font-extrabold bg-gradient-to-r from-red-600 via-red-500 to-rose-500 bg-clip-text text-transparent">
                cebinizde.
              </span>
            </h1>

            <p className="text-slate-500 text-[14.5px] sm:text-[15.5px] mt-5 max-w-md mx-auto lg:mx-0 leading-[1.65]">
              Tüm B2B operasyonlarınız artık bir dokunuş uzakta. iOS ve Android için Yetsan Panel mobil uygulaması <span className="text-slate-900 font-semibold">şimdi App Store ve Google Play'de.</span>
            </p>

            {/* Mağaza indirme butonları (gerçek linkler) */}
            <div className="flex flex-wrap justify-center lg:justify-start gap-3 mt-6">
              <StoreButton
                href={APP_STORE_URL}
                testid="appstore-badge"
                topLabel="App Store'dan"
                title="App Store"
                icon={<Apple className="text-white" size={22} />}
              />
              <StoreButton
                href={PLAY_STORE_URL}
                testid="playstore-badge"
                topLabel="Google Play'den"
                title="Google Play"
                icon={(
                  <svg className="text-white" width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 01-.61-.92V2.734a1 1 0 01.609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.198l2.807 1.626a1 1 0 010 1.73l-2.808 1.626L15.119 12l2.579-2.491zM5.864 2.658l10.937 6.332-2.302 2.302L5.864 2.658z"/>
                  </svg>
                )}
              />
              {APP_QR_URL && (
                <div className="hidden sm:flex items-center gap-2.5 bg-white border border-slate-200 rounded-2xl px-3 py-2 shadow-sm" title="Telefon kamerasıyla okutun">
                  <img src={APP_QR_URL} alt="QR" className="w-11 h-11 object-contain" />
                  <div className="text-left leading-tight">
                    <div className="text-[9px] text-slate-400 uppercase tracking-[0.2em]">Karekod</div>
                    <div className="text-[12.5px] font-semibold text-slate-800 -mt-0.5">QR ile indir</div>
                  </div>
                </div>
              )}
            </div>

            {/* Feature grid — kompakt 2x2 */}
            <div className="grid grid-cols-2 gap-2.5 mt-7 max-w-md mx-auto lg:mx-0">
              <FeatureChip icon={<Bell size={14} />} label="Anlık bildirim" />
              <FeatureChip icon={<ShoppingCart size={14} />} label="Tek tıkla sipariş" />
              <FeatureChip icon={<BarChart3 size={14} />} label="Mobil dashboard" />
              <FeatureChip icon={<Zap size={14} />} label="Hızlı erişim" />
            </div>
          </div>

          {/* ===== SAĞ — LOGIN ===== (mobilde EN ÜSTTE) */}
          <div className="order-1 lg:order-2 w-full max-w-md justify-self-center lg:justify-self-end">
            <div className="bg-white/80 backdrop-blur-xl border border-white shadow-[0_20px_60px_-15px_rgba(220,38,38,0.25)] rounded-3xl p-6 sm:p-8">
              <div className="text-center mb-7">
                <img
                  src="/yetsan-logo-yatay.png" alt="YETSAN"
                  className="h-10 w-auto object-contain mx-auto mb-3"
                  onError={(e) => { e.target.onerror = null; e.target.src = '/yetsan-logo.png'; }}
                />
                {step !== 'creds' && (
                  <div className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-red-50 text-red-600 mx-auto mb-2">
                    {step === 'otp' && <ShieldCheck size={18} />}
                    {step === 'admin_otp' && <ShieldCheck size={18} />}
                    {step === 'pending' && <Hourglass size={18} />}
                    {step === 'blocked' && <AlertTriangle size={18} />}
                    {step === 'mesai_disi_secim' && <Hourglass size={18} />}
                    {step === 'mesai_disi' && <Hourglass size={18} className="animate-pulse" />}
                  </div>
                )}
                <p className="text-slate-500 text-[12.5px] mt-1 tracking-wide">
                  {step === 'creds' && 'Hesabınıza giriş yapın'}
                  {step === 'otp' && 'Cihaz doğrulama'}
                  {step === 'admin_otp' && 'Yönetici kodu ile giriş'}
                  {step === 'pending' && 'Admin onayı bekleniyor'}
                  {step === 'blocked' && 'Güvenlik uyarısı'}
                  {step === 'mesai_disi_secim' && 'Mesai dışı onay isteği'}
                  {step === 'mesai_disi' && 'Müdür onayı bekleniyor (mesai dışı)'}
                </p>
              </div>

              {step === 'creds' && !musteriOtpModu && (
                <form onSubmit={handleLogin} className="space-y-4" data-testid="login-form">
                  <LightField icon={<Mail size={17} />} label="Email">
                    <Input data-testid="login-email-input" type="email" placeholder="kullanici@yetsan.com.tr"
                      value={email} onChange={(e) => setEmail(e.target.value)}
                      className="pl-11 py-6 bg-white border-slate-200 text-slate-900 placeholder:text-slate-400 focus:border-red-500 rounded-xl" />
                  </LightField>
                  <LightField icon={<Lock size={17} />} label="Şifre">
                    <Input data-testid="login-password-input" type="password" placeholder="••••••••"
                      value={password} onChange={(e) => setPassword(e.target.value)}
                      className="pl-11 py-6 bg-white border-slate-200 text-slate-900 placeholder:text-slate-400 focus:border-red-500 rounded-xl" />
                  </LightField>
                  <button type="submit" disabled={loading} data-testid="login-submit-button"
                    className={`w-full py-4 rounded-xl font-bold text-white text-[15px] tracking-wide transition-all flex items-center justify-center gap-2.5 mt-2 ${
                      loading ? 'bg-slate-300 cursor-not-allowed' :
                      'bg-gradient-to-r from-red-600 to-rose-600 hover:shadow-xl hover:shadow-rose-500/30 active:scale-[0.99]'
                    }`}>
                    {loading ? (
                      <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div><span>Giriş yapılıyor...</span></>
                    ) : (
                      <><span>Giriş Yap</span><LogIn size={17} /></>
                    )}
                  </button>
                  <button type="button" onClick={() => { setMusteriOtpModu(true); setMusteriOtpGonderildi(false); }}
                    className="w-full text-center text-[13px] text-red-600 hover:text-red-800 font-medium pt-1">
                    Müşteri misiniz? Kod ile giriş yapın →
                  </button>
                </form>
              )}

              {step === 'creds' && musteriOtpModu && sifreBelirleModu && (
                <form onSubmit={handleSifreBelirle} className="space-y-4" data-testid="musteri-sifre-belirle-form">
                  <div className="bg-red-50/80 border border-red-100 rounded-2xl p-4 text-[13px] text-red-900">
                    <strong className="flex items-center gap-1.5 font-semibold"><Lock size={15} /> Şifrenizi Belirleyin</strong>
                    <p className="mt-1.5 text-red-700/90">
                      İlk girişiniz için kendi şifrenizi oluşturun. Bundan sonra e-posta ve bu şifre ile giriş yapacaksınız.
                    </p>
                  </div>
                  <LightField icon={<Lock size={17} />} label="Yeni Şifre">
                    <Input type="password" placeholder="En az 6 karakter" value={yeniSifre}
                      onChange={(e) => setYeniSifre(e.target.value)} autoFocus
                      className="pl-11 py-6 bg-white border-slate-200 text-slate-900 rounded-xl" />
                  </LightField>
                  <LightField icon={<Lock size={17} />} label="Yeni Şifre (Tekrar)">
                    <Input type="password" placeholder="Şifreyi tekrar girin" value={yeniSifreTekrar}
                      onChange={(e) => setYeniSifreTekrar(e.target.value)}
                      className="pl-11 py-6 bg-white border-slate-200 text-slate-900 rounded-xl" />
                  </LightField>
                  <button type="submit" disabled={loading}
                    className={`w-full py-4 rounded-xl font-bold text-white text-[15px] transition-all flex items-center justify-center gap-2.5 mt-2 ${
                      loading ? 'bg-slate-300 cursor-not-allowed' : 'bg-gradient-to-r from-red-600 to-rose-600 hover:shadow-xl active:scale-[0.99]'
                    }`}>
                    {loading ? (
                      <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div><span>Kaydediliyor...</span></>
                    ) : (
                      <><span>Şifreyi Kaydet ve Giriş Yap</span><LogIn size={17} /></>
                    )}
                  </button>
                </form>
              )}

              {step === 'creds' && musteriOtpModu && !sifreBelirleModu && (
                <form onSubmit={musteriOtpGonderildi ? handleMusteriOtpGiris : handleMusteriOtpIste} className="space-y-4" data-testid="musteri-otp-form">
                  <div className="bg-emerald-50/80 border border-emerald-100 rounded-2xl p-4 text-[13px] text-emerald-900">
                    <strong className="flex items-center gap-1.5 font-semibold"><ShieldCheck size={15} /> Müşteri Girişi (Şifresiz)</strong>
                    <p className="mt-1.5 text-emerald-700/90">
                      {musteriOtpGonderildi
                        ? `${email} adresine gelen 6 haneli kodu girin (30 dk geçerli).`
                        : 'E-postanızı girin, size tek kullanımlık giriş kodu gönderelim.'}
                    </p>
                  </div>
                  <LightField icon={<Mail size={17} />} label="Email">
                    <Input type="email" placeholder="ornek@firma.com" value={email}
                      onChange={(e) => setEmail(e.target.value)} disabled={musteriOtpGonderildi}
                      className="pl-11 py-6 bg-white border-slate-200 text-slate-900 rounded-xl disabled:bg-slate-100" />
                  </LightField>
                  {musteriOtpGonderildi && (
                    <LightField icon={<Lock size={17} />} label="Giriş Kodu">
                      <Input type="text" inputMode="numeric" maxLength={6} placeholder="6 haneli kod"
                        value={musteriOtp} onChange={(e) => setMusteriOtp(e.target.value.replace(/\D/g, ''))}
                        className="pl-11 py-6 bg-white border-slate-200 text-slate-900 rounded-xl tracking-[0.3em] text-center font-mono text-lg" />
                    </LightField>
                  )}
                  <button type="submit" disabled={loading}
                    className={`w-full py-4 rounded-xl font-bold text-white text-[15px] transition-all flex items-center justify-center gap-2.5 mt-2 ${
                      loading ? 'bg-slate-300 cursor-not-allowed' : 'bg-gradient-to-r from-emerald-600 to-teal-600 hover:shadow-xl active:scale-[0.99]'
                    }`}>
                    {loading ? (
                      <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div><span>İşleniyor...</span></>
                    ) : musteriOtpGonderildi ? (
                      <><span>Giriş Yap</span><LogIn size={17} /></>
                    ) : (
                      <><span>Kod Gönder</span><Mail size={17} /></>
                    )}
                  </button>
                  {musteriOtpGonderildi && (
                    <button type="button" onClick={handleMusteriOtpIste} disabled={loading}
                      className="w-full text-center text-[13px] text-emerald-700 hover:text-emerald-900 font-medium">
                      Yeni kod gönder
                    </button>
                  )}
                  <button type="button" onClick={() => { setMusteriOtpModu(false); setMusteriOtpGonderildi(false); setMusteriOtp(''); }}
                    className="w-full text-center text-[13px] text-slate-500 hover:text-slate-700">
                    ← Şifre ile girişe dön
                  </button>
                </form>
              )}

              {step === 'otp' && (
                <form onSubmit={handleVerifyOtp} className="space-y-4" data-testid="otp-form">
                  <div className="bg-red-50/80 border border-red-100 rounded-2xl p-4 text-[13px] text-red-900">
                    <strong className="flex items-center gap-1.5 font-semibold"><ShieldCheck size={15}/> Yeni Cihaz Doğrulaması</strong>
                    <p className="mt-1.5 text-red-700/90">
                      6 haneli kod <strong>{KANAL_AD[otpKanal] || 'e-posta'}</strong> ile{' '}
                      <strong>{otpHedef || maskedEmail || email}</strong> adresine gönderildi.
                    </p>
                  </div>

                  {/* 🆕 Kanal seçici — yalnız gerçek seçenek varsa (≥2 aktif kanal) göster.
                      Yapılandırılmamış kanallar (ör. WhatsApp) hiç görünmez. */}
                  {kanallar.filter((c) => c.aktif).length >= 2 && (
                    <div>
                      <FieldLabel>Kod nereye gelsin?</FieldLabel>
                      <div className="grid grid-cols-3 gap-2">
                        {['sms', 'whatsapp', 'mail'].map((k) => {
                          const c = kanallar.find((x) => x.kanal === k);
                          if (!c?.aktif) return null;  // pasif/yapılandırılmamış kanalı gizle
                          const secili = otpKanal === k;
                          const Icon = k === 'sms' ? MessageSquare : k === 'whatsapp' ? MessageCircle : Mail;
                          return (
                            <button type="button" key={k} disabled={loading}
                              onClick={() => requestOtp(k)}
                              title={c?.hedef || ''}
                              className={`flex flex-col items-center gap-1 py-2.5 rounded-xl border text-[12px] font-semibold transition ${
                                secili ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                                : 'border-slate-200 bg-white text-slate-700 hover:border-red-300 hover:bg-red-50'
                              }`}>
                              <Icon size={16} />
                              <span>{KANAL_AD[k]}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  <div>
                    <FieldLabel>Cihaz Etiketi</FieldLabel>
                    <Input data-testid="otp-device-label-input" type="text" placeholder="Örn: Muhasebe Bilgisayarı"
                      value={deviceLabel} onChange={(e) => setDeviceLabel(e.target.value)}
                      className="py-5 bg-white border-slate-200 text-slate-900 rounded-xl" />
                  </div>
                  <div>
                    <FieldLabel>Aktivasyon Kodu</FieldLabel>
                    <Input data-testid="otp-code-input" type="text" inputMode="numeric" maxLength={6}
                      placeholder="• • • • • •"
                      value={otpCode} onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
                      className="py-6 text-center text-2xl tracking-[0.6em] font-mono bg-white border-slate-200 text-slate-900 rounded-xl" />
                  </div>
                  <button type="submit" disabled={loading || otpCode.length < 6} data-testid="otp-verify-button"
                    className={`w-full py-4 rounded-xl font-bold text-white text-[15px] transition flex items-center justify-center gap-2 ${
                      loading || otpCode.length < 6 ? 'bg-slate-300 cursor-not-allowed' :
                      'bg-gradient-to-r from-emerald-500 to-green-600 hover:shadow-xl hover:shadow-emerald-500/30'
                    }`}>
                    {loading ? 'Doğrulanıyor...' : <><ShieldCheck size={17} /><span>Doğrula ve Giriş Yap</span></>}
                  </button>
                  <div className="flex justify-between text-[13px]">
                    <button type="button" onClick={resetFlow} className="text-slate-500 hover:text-slate-900 transition">← Geri</button>
                    <button type="button" onClick={() => requestOtp(otpKanal)} disabled={loading} className="text-red-600 hover:text-red-800 transition">Kodu tekrar gönder</button>
                  </div>
                  <div className="border-t border-slate-100 pt-3 mt-1">
                    <button type="button" onClick={() => { setStep('admin_otp'); setOtpCode(''); }}
                      className="w-full text-[12px] text-slate-500 hover:text-red-600 underline underline-offset-2 transition" data-testid="email-not-received-link">
                      Email gelmedi mi? Yöneticinizden geçici kod alın
                    </button>
                  </div>
                </form>
              )}

              {step === 'admin_otp' && (
                <form onSubmit={handleVerifyAdminOtp} className="space-y-4" data-testid="admin-otp-form">
                  <div className="bg-rose-50/80 border border-rose-100 rounded-2xl p-4 text-[13px] text-rose-900">
                    <strong className="font-semibold">Yönetici Tarafından Verilen Kod</strong>
                    <p className="mt-1.5 text-rose-700/90">
                      Yöneticinizi arayıp <strong>"Cihaz aktivasyon kodu istiyorum"</strong> deyin.
                    </p>
                  </div>
                  <div>
                    <FieldLabel>Cihaz Etiketi</FieldLabel>
                    <Input data-testid="admin-otp-device-label" type="text"
                      value={deviceLabel} onChange={(e) => setDeviceLabel(e.target.value)}
                      className="py-5 bg-white border-slate-200 text-slate-900 rounded-xl" />
                  </div>
                  <div>
                    <FieldLabel>Yönetici Kodu (6 hane)</FieldLabel>
                    <Input data-testid="admin-otp-code-input" type="text" inputMode="numeric" maxLength={6}
                      placeholder="• • • • • •"
                      value={otpCode} onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
                      className="py-6 text-center text-2xl tracking-[0.6em] font-mono bg-white border-slate-200 text-slate-900 rounded-xl" />
                    <p className="text-[11px] text-slate-500 mt-1.5">Bu kod sadece 30 dakika geçerlidir.</p>
                  </div>
                  <button type="submit" disabled={loading || otpCode.length < 6} data-testid="admin-otp-verify-button"
                    className={`w-full py-4 rounded-xl font-bold text-white text-[15px] transition flex items-center justify-center gap-2 ${
                      loading || otpCode.length < 6 ? 'bg-slate-300 cursor-not-allowed' :
                      'bg-gradient-to-r from-rose-500 to-rose-600 hover:shadow-xl hover:shadow-rose-500/30'
                    }`}>
                    {loading ? 'Doğrulanıyor...' : <><ShieldCheck size={17} /><span>Kodu Doğrula ve Giriş Yap</span></>}
                  </button>
                  <button type="button" onClick={() => setStep('otp')} className="w-full text-[12px] text-slate-500 hover:text-slate-900 transition">
                    ← Email kodu girme adımına dön
                  </button>
                </form>
              )}

              {step === 'pending' && (
                <div className="space-y-4">
                  <div className="bg-amber-50/80 border border-amber-100 rounded-2xl p-5">
                    <Hourglass className="text-amber-600 mb-2" size={26} />
                    <h3 className="font-semibold text-amber-900 text-[15px]">Onay Bekleniyor</h3>
                    <p className="text-[13px] text-amber-800 mt-2 leading-relaxed">{statusMsg}</p>
                  </div>
                  <div className="bg-red-50/80 border border-red-100 rounded-2xl p-4 text-[13px] text-red-900">
                    <strong className="font-semibold">Sonraki Adım:</strong>
                    <p className="mt-1.5">Yöneticinizi arayıp <strong>yeni cihaz doğrulaması</strong> talep ettiğinizi bildirin.</p>
                  </div>
                  <button onClick={resetFlow} data-testid="pending-back-button"
                    className="w-full py-3.5 rounded-xl font-medium text-slate-700 bg-slate-50 hover:bg-slate-100 transition border border-slate-200">
                    ← Başa Dön
                  </button>
                </div>
              )}

              {step === 'blocked' && (
                <div className="space-y-4">
                  <div className="bg-red-50/80 border border-red-100 rounded-2xl p-5">
                    <AlertTriangle className="text-red-600 mb-2" size={26} />
                    <h3 className="font-semibold text-red-900 text-[15px]">Anormal Aktivite Tespit Edildi</h3>
                    <p className="text-[13px] text-red-800 mt-2 leading-relaxed">{statusMsg}</p>
                  </div>
                  <div className="bg-slate-50 border border-slate-100 rounded-2xl p-4 text-[12px] text-slate-700">
                    <strong className="font-semibold">Güvenliğiniz için:</strong>
                    <ul className="mt-2 list-disc list-inside space-y-1">
                      <li>Şifrenizi değiştirin</li>
                      <li>Yöneticinizle iletişime geçin</li>
                      <li>Aktif oturumunuz başka bir IP'den hâlâ açık olabilir</li>
                    </ul>
                  </div>
                  <button onClick={resetFlow} data-testid="blocked-back-button"
                    className="w-full py-3.5 rounded-xl font-medium text-slate-700 bg-slate-50 hover:bg-slate-100 transition border border-slate-200">
                    ← Başa Dön
                  </button>
                </div>
              )}

              {step === 'mesai_disi_secim' && (
                <div className="space-y-4" data-testid="mesai-disi-secim-step">
                  <div className="bg-amber-50/80 border border-amber-100 rounded-2xl p-4">
                    <AlertTriangle className="text-amber-600 mb-1" size={22} />
                    <h3 className="font-semibold text-amber-900 text-[14px]">Mesai Dışı Giriş</h3>
                    <p className="text-[12px] text-amber-800 mt-1">{mesaiDisiNeden}</p>
                  </div>

                  {/* Süre seçimi */}
                  <div>
                    <label className="block text-[12px] font-semibold text-slate-700 mb-2">
                      Ne kadar sürelik izin istiyorsun?
                    </label>
                    <div className="grid grid-cols-3 gap-2">
                      {Object.entries(mesaiDisiDurations).map(([dk, label]) => (
                        <button
                          key={dk}
                          type="button"
                          data-testid={`mesai-disi-dk-${dk}`}
                          onClick={() => setMesaiDisiSecimDk(parseInt(dk))}
                          className={`px-2 py-2 rounded-lg text-[11.5px] font-medium border transition ${
                            mesaiDisiSecimDk === parseInt(dk)
                              ? 'bg-rose-600 border-rose-600 text-white shadow'
                              : 'bg-white border-slate-200 text-slate-700 hover:border-rose-300'
                          }`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Onaylayıcı seçimi */}
                  <div>
                    <label className="block text-[12px] font-semibold text-slate-700 mb-2">
                      Onayı kimden istiyorsun?
                    </label>
                    <div className="space-y-1.5 max-h-52 overflow-y-auto pr-1">
                      {mesaiDisiOnaylayicilar.length === 0 && (
                        <div className="text-[12px] text-slate-500 italic">Onaylayıcı bulunamadı</div>
                      )}
                      {mesaiDisiOnaylayicilar.map(o => (
                        <button
                          key={o.id}
                          type="button"
                          data-testid={`mesai-disi-onaylayici-${o.id}`}
                          onClick={() => setMesaiDisiSecimOnaylayici(o.id)}
                          className={`w-full text-left px-3 py-2 rounded-lg border transition flex items-center justify-between gap-2 ${
                            mesaiDisiSecimOnaylayici === o.id
                              ? 'bg-rose-50 border-rose-400 ring-2 ring-rose-200'
                              : 'bg-white border-slate-200 hover:border-rose-200'
                          }`}
                        >
                          <div className="min-w-0">
                            <div className="text-[12.5px] font-semibold text-slate-800 flex items-center gap-1.5">
                              {o.name}
                              {o.oncelik && (
                                <span className="px-1.5 py-0.5 bg-rose-100 text-rose-700 rounded-full text-[10px] font-semibold">
                                  Müdürün
                                </span>
                              )}
                              {o.has_otp_email && (
                                <span className="px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded-full text-[10px] font-medium" title="Atanmış OTP mail kullanılır">
                                  ✓ özel mail
                                </span>
                              )}
                            </div>
                            <div className="text-[11px] text-slate-500 mt-0.5">
                              {o.role}{o.firma && ` • ${o.firma}`}
                            </div>
                            <div className="text-[11px] font-mono text-red-700 mt-0.5 truncate">
                              📧 {o.target_email_masked}
                            </div>
                          </div>
                          {mesaiDisiSecimOnaylayici === o.id && (
                            <ShieldCheck className="text-rose-600 flex-shrink-0" size={18} />
                          )}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="bg-slate-50 border border-slate-100 rounded-xl p-3 text-[11.5px] text-slate-600">
                    💡 Seçtiğin kişiye <strong>{mesaiDisiDurations[mesaiDisiSecimDk]}</strong> süreli giriş izni
                    için mail gönderilecek. O kişi süreyi onaylar veya değiştirebilir.
                  </div>

                  <button
                    type="button"
                    data-testid="mesai-disi-submit-btn"
                    onClick={submitMesaiDisiIstek}
                    disabled={mesaiDisiSubmitting || !mesaiDisiSecimOnaylayici}
                    className="w-full py-3.5 rounded-xl font-semibold text-white bg-gradient-to-r from-rose-600 to-red-600 hover:from-rose-700 hover:to-red-700 disabled:opacity-50 transition shadow-md"
                  >
                    {mesaiDisiSubmitting ? 'Gönderiliyor...' : '📧 Onay İste'}
                  </button>

                  <button
                    type="button"
                    onClick={() => { setStep('creds'); setMesaiDisiOnaylayicilar([]); }}
                    className="w-full py-2.5 rounded-xl font-medium text-slate-700 bg-slate-50 hover:bg-slate-100 transition border border-slate-200 text-[13px]"
                  >
                    ← Vazgeç
                  </button>
                </div>
              )}

              {step === 'mesai_disi' && (
                <div className="space-y-4" data-testid="mesai-disi-step">
                  <div className="bg-rose-50/80 border border-rose-100 rounded-2xl p-5">
                    <Hourglass className="text-rose-600 mb-2 animate-pulse" size={26} />
                    <h3 className="font-semibold text-rose-900 text-[15px]">Onay Bekleniyor</h3>
                    <p className="text-[13px] text-rose-800 mt-2 leading-relaxed">
                      Onaylayıcıya istek gönderildi. Onayladığı süre kadar sisteme erişebileceksiniz.
                    </p>
                    {mesaiDisiInfo?.talep_edilen_dk && mesaiDisiDurations[mesaiDisiInfo.talep_edilen_dk] && (
                      <p className="text-[12px] text-rose-700 mt-2">
                        <strong>Talep ettiğin süre:</strong> {mesaiDisiDurations[mesaiDisiInfo.talep_edilen_dk]}
                      </p>
                    )}
                    {mesaiDisiInfo?.neden && (
                      <p className="text-[12px] text-rose-700 mt-1">
                        <strong>Sebep:</strong> {mesaiDisiInfo.neden}
                      </p>
                    )}
                  </div>

                  {mesaiDisiInfo?.manager_email_masked && (
                    <div className="bg-red-50/80 border border-red-100 rounded-2xl p-4 text-[13px] text-red-900">
                      <div className="flex items-center gap-2 mb-1">
                        <Mail size={14} />
                        <strong className="font-semibold">İstek gönderildi:</strong>
                      </div>
                      <p className="text-[12.5px]">
                        {mesaiDisiInfo.manager_name}
                        {mesaiDisiInfo.manager_role && <span className="text-red-600"> ({mesaiDisiInfo.manager_role})</span>}
                      </p>
                      <p className="font-mono text-[11.5px] text-red-700">{mesaiDisiInfo.manager_email_masked}</p>
                    </div>
                  )}

                  <div className="bg-amber-50/60 border border-amber-100 rounded-2xl p-3 text-[12px] text-amber-800">
                    ⏳ Bu ekranı açık tutun, onaylandığında otomatik giriş yapılacak.
                    Süre <strong>{mesaiDisiInfo?.expires_in_minutes || 30} dk</strong>.
                  </div>

                  {/* 🆕 Tekrar gönder / Başka kişi seç butonları */}
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={async () => {
                        if (mesaiPollRef.current) clearInterval(mesaiPollRef.current);
                        // Aynı seçimle yeniden gönder — backend eski isteği iptal eder, yenisini oluşturur
                        await submitMesaiDisiIstek();
                      }}
                      disabled={mesaiDisiSubmitting}
                      data-testid="mesai-disi-resend"
                      className="py-2.5 rounded-xl font-medium text-rose-700 bg-rose-50 hover:bg-rose-100 transition border border-rose-200 text-[12.5px] disabled:opacity-50"
                    >
                      📧 Maili Tekrar Gönder
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        if (mesaiPollRef.current) clearInterval(mesaiPollRef.current);
                        setMesaiDisiInfo(null);
                        setStep('mesai_disi_secim');
                      }}
                      data-testid="mesai-disi-change-approver"
                      className="py-2.5 rounded-xl font-medium text-red-700 bg-red-50 hover:bg-red-100 transition border border-red-200 text-[12.5px]"
                    >
                      ↻ Başka Kişi Seç
                    </button>
                  </div>

                  <button
                    onClick={() => {
                      if (mesaiPollRef.current) clearInterval(mesaiPollRef.current);
                      setStep('creds');
                      setMesaiDisiInfo(null);
                    }}
                    data-testid="mesai-disi-cancel"
                    className="w-full py-3.5 rounded-xl font-medium text-slate-700 bg-slate-50 hover:bg-slate-100 transition border border-slate-200"
                  >
                    ← Vazgeç
                  </button>
                </div>
              )}
            </div>

            <p className="text-center text-[10.5px] text-slate-400 mt-5 tracking-[0.15em]">
              © 2026 YETSAN — TÜM HAKLARI SAKLIDIR
            </p>
            {/* Yasal metinler (2026-10-23) — otoradyator.com/kvkk-aydinlatma · /cerez-politikasi */}
            <p className="text-center text-[11px] text-slate-400 mt-2 space-x-3">
              <a href="https://otoradyator.com/kvkk-aydinlatma" target="_blank" rel="noopener noreferrer" className="hover:text-slate-600 underline underline-offset-2">KVKK Aydınlatma Metni</a>
              <a href="https://otoradyator.com/cerez-politikasi" target="_blank" rel="noopener noreferrer" className="hover:text-slate-600 underline underline-offset-2">Çerez Politikası</a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Mağaza indirme butonu — link varsa tıklanır, yoksa "Yakında mağazada" pasif görünür
const StoreButton = ({ href, topLabel, title, icon, testid }) => {
  const aktif = !!href;
  const inner = (
    <>
      {icon}
      <div className="text-left leading-tight">
        <div className="text-[9px] text-slate-400 uppercase tracking-[0.2em]">{aktif ? topLabel : 'Yakında'}</div>
        <div className="text-[14px] font-semibold text-white -mt-0.5">{title}</div>
      </div>
    </>
  );
  const cls = `flex items-center gap-2.5 rounded-2xl px-4 py-2.5 shadow-md transition-all duration-300 ${
    aktif ? 'bg-slate-900 hover:bg-black hover:shadow-xl hover:-translate-y-0.5 cursor-pointer' : 'bg-slate-400/80 cursor-not-allowed'
  }`;
  if (!aktif) return <div className={cls} data-testid={testid} title="Yakında mağazada">{inner}</div>;
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className={cls} data-testid={testid} title={`${title} — indir`}>
      {inner}
    </a>
  );
};

const FeatureChip = ({ icon, label }) => (
  <div className="flex items-center gap-2.5 bg-white/80 border border-slate-200/70 rounded-xl px-3 py-2.5 hover:bg-white hover:shadow-md hover:border-red-200 transition-all shadow-sm">
    <div className="text-red-600 flex-shrink-0">{icon}</div>
    <span className="text-[13px] text-slate-700 font-semibold">{label}</span>
  </div>
);

const FieldLabel = ({ children }) => (
  <label className="block text-[11px] font-bold text-slate-600 mb-2 uppercase tracking-[0.15em]">{children}</label>
);

const LightField = ({ icon, label, children }) => (
  <div>
    <FieldLabel>{label}</FieldLabel>
    <div className="relative">
      <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 z-10">{icon}</div>
      {children}
    </div>
  </div>
);

export default Login;
